import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaHome, FaPlus, FaSearch, FaEdit, FaEye, FaTrash, FaSyncAlt } from 'react-icons/fa';
import ClienteModal from './ClienteModal';
import ModalFaturamento from './ModalFaturamento';

const ClienteControlScreen = () => {
    // Estados
    const [nomeFilter, setNomeFilter] = useState('');
    const [cpfCnpjFilter, setCpfCnpjFilter] = useState('');
    const [selectedEmitenteId, setSelectedEmitenteId] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('Ativo');
    const [allClientes, setAllClientes] = useState([]);
    const [currentClientes, setCurrentClientes] = useState([]);
    const [emitentes, setEmitentes] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    // const [clientesPerPage] = useState(10);
    const [clientesPerPage, setClientesPerPage] = useState(12);
    // const [totalPages, setTotalPages] = useState(0);
    const totalPages = useMemo(
        () => Math.ceil(allClientes.length / clientesPerPage) || 0,
        [allClientes.length, clientesPerPage]
    );

    const pageList = useMemo(
        () => makePageList(totalPages, currentPage, 2),
        [totalPages, currentPage]
    );

    const [isGerandoFat, setIsGerandoFat] = useState(false);

    const [loading, setLoading] = useState(false);
    const [loadingEmitentes, setLoadingEmitentes] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedCliente, setSelectedCliente] = useState(null);

    const [showFatModal, setShowFatModal] = useState(false);
    const handleAbrirFaturamento = () => setShowFatModal(true);

    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const navigate = useNavigate();

    // token e id do emitente
    const authHeader = useMemo(
        () => ({ Authorization: `Bearer ${localStorage.getItem('userToken')}` }),
        []
    );

    // helper para montar lista de páginas compacta (com "…")
    function makePageList(total, current, delta = 2) {
        if (total <= 1) return [1];
        const range = [1];
        const left = Math.max(2, current - delta);
        const right = Math.min(total - 1, current + delta);
        if (left > 2) range.push('…');
        for (let i = left; i <= right; i++) range.push(i);
        if (right < total - 1) range.push('…');
        range.push(total);
        return range;
    }

    useEffect(() => {
        const storedEmitenteId = localStorage.getItem('userIdEmitente');
        if (storedEmitenteId) {
            setSelectedEmitenteId(String(storedEmitenteId));
        } else {
            toast.error('ID do emitente não encontrado. Faça login novamente.');
        }
    }, []);

    useEffect(() => {
        const fetchEmitentes = async () => {
            setLoadingEmitentes(true);
            try {
                const { data } = await axios.get(
                    `${URL_BACKEND}/emitentesAll/999/999`,
                    { headers: authHeader }
                );
                setEmitentes(Array.isArray(data) ? data : []);
            } catch (error) {
                toast.error('Erro ao carregar a lista de emitentes.');
            } finally {
                setLoadingEmitentes(false);
            }
        };
        fetchEmitentes();
    }, [URL_BACKEND, authHeader]);

    const fetchClientes = async () => {
        if (!selectedEmitenteId) {
            toast.error('Selecione um emitente para buscar os clientes.');
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${URL_BACKEND}/clientes/${selectedStatus}/${selectedEmitenteId}?nome=${encodeURIComponent(
                    nomeFilter || ''
                )}&cpfCnpj=${encodeURIComponent(cpfCnpjFilter || '')}`,
                { headers: authHeader }
            );
            const arr = Array.isArray(data) ? data : [];
            setAllClientes(arr);
            // setTotalPages(Math.ceil(arr.length / clientesPerPage));
            setCurrentPage(1);
            toast.success(`Clientes carregados com sucesso! (${arr.length} registros)`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erro ao carregar clientes.');
            setAllClientes([]);
            // setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    // Paginação local
    useEffect(() => {
        const indexOfLast = currentPage * clientesPerPage;
        const indexOfFirst = indexOfLast - clientesPerPage;
        setCurrentClientes(allClientes.slice(indexOfFirst, indexOfLast));
    }, [allClientes, currentPage, clientesPerPage]);

    // Auto-busca ao trocar emitente
    useEffect(() => {
        if (selectedEmitenteId) fetchClientes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEmitenteId]);

    const goToPage = (page) => setCurrentPage(page);

    const handleCreate = () => { setModalMode('create'); setSelectedCliente(null); setShowModal(true); };
    const handleEdit = (cliente) => { setModalMode('edit'); setSelectedCliente(cliente); setShowModal(true); };
    const handleView = (cliente) => { setModalMode('view'); setSelectedCliente(cliente); setShowModal(true); };

    const handleDelete = async (id) => {
        if (!window.confirm(`Excluir cliente ID ${id}?`)) return;
        const toastId = toast.loading('Excluindo...');
        try {
            await axios.delete(`${URL_BACKEND}/cliente/${id}`, { headers: authHeader });
            toast.success('Cliente excluído!', { id: toastId });
            fetchClientes();
        } catch (error) {
            toast.error('Erro ao excluir.', { id: toastId });
        }
    };

    const clearFilters = () => {
        setNomeFilter('');
        setCpfCnpjFilter('');
    };

    function formatCpfCnpj(value) {
        if (!value) return '';

        // Remove tudo que não for número
        const digits = value.replace(/\D/g, '');

        if (digits.length <= 11) {
            // CPF -> 000.000.000-00
            return digits
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        } else {
            // CNPJ -> 00.000.000/0000-00
            return digits
                .replace(/^(\d{2})(\d)/, '$1.$2')
                .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
                .replace(/\.(\d{3})(\d)/, '.$1/$2')
                .replace(/(\d{4})(\d)/, '$1-$2');
        }
    }


    const handleConfirmarFaturamento = async ({ ano, mes, yyyymm, label }) => {
        try {
            if (!selectedEmitenteId) {
                toast.error('Selecione um emitente.');
                return;
            }

            setIsGerandoFat(true);

            await axios.post(
                `${URL_BACKEND}/faturamento/gerar`,
                {
                    emitente: Number(selectedEmitenteId),
                    ano: Number(ano),
                    mes: Number(mes) // 1..12
                },
                { headers: authHeader }
            );

            toast.success(`Faturamento de ${label} solicitado!`);
        } catch (e) {
            const msg = e?.response?.data?.message || 'Erro ao gerar faturamento.';
            toast.error(msg);
        } finally {
            setShowFatModal(false);
            setIsGerandoFat(false);
        }
    };


    const handleStatus = async (id) => {
        if (!window.confirm(`Confirma a operação ?`)) return;
        try {
            const response = await axios({
                method: 'PUT',
                url: `${URL_BACKEND}/statuscliente/${id}`,
                data: { 'id': '${id}' },
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('userToken')}`,
                },
            });

            if (response.status === 200 || response.status === 201) {
                alert(`Cliente atualizado com sucesso!`);
            }
        } catch (error) {
            console.error('Erro ao salvar cliente:', error);
            alert(error.response?.data?.message || `Erro ao atualizar cliente.`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={ui.container}>

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 20px',
                borderRadius: '12px',
                background: 'linear-gradient(to right, #667eea, #764ba2)',
                color: '#fff',
                marginBottom: '24px'
            }}>
                <h1 style={{ fontSize: '1.2rem', fontWeight: '600' }}>Controle de Clientes</h1>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'transparent',
                        border: '1px solid rgba(255,255,255,0.5)',
                        borderRadius: '8px',
                        padding: '6px 12px',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                    }}
                >
                    <FaHome /> Voltar
                </button>
            </div>

            <div style={ui.filters}>
                <div style={ui.filterGroup}>
                    <label style={ui.label}>Nome</label>
                    <input
                        style={ui.input}
                        value={nomeFilter}
                        onChange={e => setNomeFilter(e.target.value)}
                        placeholder="Ex.: Maria Contábil"
                    />
                </div>
                <div style={ui.filterGroup}>
                    <label style={ui.label}>CPF/CNPJ</label>
                    <input
                        style={ui.input}
                        value={cpfCnpjFilter}
                        onChange={e => setCpfCnpjFilter(e.target.value)}
                        placeholder="Pode ser parcial"
                    />
                </div>

                <div style={ui.filterGroup}>
                    <label style={ui.label}>Status</label>
                    <select
                        style={ui.select}
                        value={selectedStatus}
                        onChange={e => setSelectedStatus(String(e.target.value))}
                        disabled={loadingEmitentes}
                    >
                        <option value="Todos">Todos</option>
                        <option value="Ativo">Ativos</option>
                        <option value="Inativo">Inativos</option>
                    </select>
                </div>

                <div style={ui.filterGroup}>
                    <label style={ui.label}>Emitente</label>
                    <select
                        style={ui.select}
                        value={selectedEmitenteId}
                        onChange={e => setSelectedEmitenteId(String(e.target.value))}
                        disabled={loadingEmitentes}
                    >
                        <option value="">{loadingEmitentes ? 'Carregando...' : 'Selecione...'}</option>
                        {emitentes.map(e => (
                            <option key={e.id} value={String(e.id)}>{e.nome}</option>
                        ))}
                    </select>
                </div>

                <div style={ui.actionsRow}>
                    <button style={ui.buttonPrimary} onClick={fetchClientes} disabled={loading}>
                        {loading ? <FaSpinner className="fa-spin" /> : <><FaSearch /> Buscar</>}
                    </button>
                    {/* <button style={ui.buttonLight} onClick={clearFilters} disabled={loading}>
                        Limpar
                    </button> */}
                    <button style={ui.buttonFatur} onClick={handleAbrirFaturamento} disabled={loading}>
                        Gerar Faturamento
                    </button>
                    <button style={ui.buttonGreen} onClick={handleCreate}>
                        <FaPlus /> Adicionar
                    </button>
                </div>
            </div>

            {/* Tabela aprimorada */}
            <div style={ui.tableCard}>
                {loading ? (
                    <p>Carregando...</p>
                ) : currentClientes.length === 0 ? (
                    <p style={{ color: 'red', padding: 8 }}>Nenhum cliente encontrado.</p>
                ) : (
                    <div style={ui.tableScroll}>
                        <table style={ui.table}>
                            <thead style={ui.thead}>
                                <tr>
                                    {/* <th style={{ width: '160px', whiteSpace: 'nowrap' }}>CNPJ/CPF</th> */}
                                    <th style={ui.th}>CNPJ/CPF</th>
                                    <th style={ui.th}>Nome</th>
                                    <th style={ui.th}>Cidade</th>
                                    <th style={ui.th}>Boleto</th>
                                    <th style={ui.th}>NFSe</th>
                                    <th style={ui.th}>Valor</th>
                                    <th style={ui.thActions}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentClientes.map((c, idx) => (
                                    <tr
                                        key={c.id}
                                        style={{
                                            ...ui.tr,
                                            backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb'
                                        }}
                                    >
                                        <td style={ui.tdMono}>
                                            {formatCpfCnpj(c.tipo_pessoa === 'PESSOA_FISICA' ? c.cpf : c.cnpj)}
                                        </td>
                                        <td style={ui.tdClamp}>{c.nome}</td>
                                        {/* <td style={ui.tdClamp}>{c.email1}</td> */}
                                        <td style={ui.tdClamp}>{c.nome_cidade}</td>
                                        <td style={ui.tdClamp}>{c.gerar_boleto}</td>
                                        <td style={ui.tdClamp}>{c.gerar_nfse}</td>
                                        <td style={ui.tdClamp}>{c.valor_servico}</td>
                                        <td style={ui.tdActions}>
                                            <button title="Visualizar" style={ui.actionView} onClick={() => handleView(c)}><FaEye /></button>
                                            <button title="Editar" style={ui.actionEdit} onClick={() => handleEdit(c)}><FaEdit /></button>
                                            <button title="Excluir" style={ui.actionDelete} onClick={() => handleDelete(c.id)}><FaTrash /></button>
                                            <button title="Ativar/Inativar" style={ui.actionDelete} onClick={() => handleStatus(c.id)}><FaSyncAlt /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <colgroup>
                                <col style={{ width: '160px' }} />   {/* CNPJ */}
                                <col style={{ width: '200px' }} />    {/* Nome */}
                                <col style={{ width: '150px' }} />   {/* Cidade */}
                                <col style={{ width: '80px' }} />   {/* Gera Boleto */}
                                <col style={{ width: '80px' }} />   {/* Gera Nota */}
                                <col style={{ width: '80px' }} />   {/* Valor */}
                                <col style={{ width: '120px' }} />   {/* Ações */}
                            </colgroup>
                        </table>
                    </div>
                )}
            </div>


            {totalPages > 1 && (
                <div style={{ ...ui.pagination, flexWrap: 'wrap', gap: 6 }}>
                    <button
                        style={ui.page}
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                        aria-label="Primeira página"
                    >
                        «
                    </button>
                    <button
                        style={ui.page}
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        aria-label="Página anterior"
                    >
                        ‹
                    </button>

                    {pageList.map((p, i) =>
                        p === '…' ? (
                            <span key={`dots-${i}`} style={ui.pageEllipsis}>…</span>
                        ) : (
                            <button
                                key={p}
                                style={currentPage === p ? ui.pageActive : ui.page}
                                onClick={() => setCurrentPage(p)}
                            >
                                {p}
                            </button>
                        )
                    )}

                    <button
                        style={ui.page}
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        aria-label="Próxima página"
                    >
                        ›
                    </button>
                    <button
                        style={ui.page}
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        aria-label="Última página"
                    >
                        »
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8 }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>por página</span>
                        <select
                            value={clientesPerPage}
                            onChange={e => { setClientesPerPage(Number(e.target.value)); setCurrentPage(1); }}
                            style={ui.select}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>
            )}


            <ClienteModal
                show={showModal}
                onClose={() => setShowModal(false)}
                cliente={selectedCliente}
                onSave={fetchClientes}
                mode={modalMode}
            />

            <ModalFaturamento
                show={showFatModal}
                onClose={() => setShowFatModal(false)}
                onConfirm={handleConfirmarFaturamento}
                loading={isGerandoFat}
            />


        </div>
    );
};

// 🎨 Estilo
const ui = {
    container: {
        padding: 24,
        backgroundColor: '#f8fafc',
        minHeight: '95vh',
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        boxSizing: 'border-box',
        marginBottom: 40
        // paddingBottom: 40 // espaço extra no fim
    },
    header: { display: 'flex', justifyContent: 'space-between', marginBottom: 24, alignItems: 'center' },
    title: { fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' },
    homeButton: { background: '#6b7280', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer' },

    // filters: {
    //     display: 'grid',
    //     gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    //     gap: 16,
    //     background: '#fff',
    //     padding: 16,
    //     borderRadius: 12,
    //     border: '1px solid #e5e7eb',
    //     marginBottom: 24
    // },

    filters: {
        display: 'flex',
        flexWrap: 'nowrap',
        gap: 16,
        background: '#fff',
        padding: 16,
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        marginBottom: 24,
        overflowX: 'auto', // Adicionei isso para barras de rolagem horizontais, se necessário
    },
    filterGroup: { display: 'flex', flexDirection: 'column' },
    label: { fontSize: 14, fontWeight: '500', marginBottom: 4 },
    input: {
        padding: '8px 12px', border: '2px solid #e5e7eb', borderRadius: 8
    },
    select: { padding: '8px 12px', border: '2px solid #e5e7eb', borderRadius: 8 },
    actionsRow: { display: 'flex', gap: 8, alignItems: 'center' },

    buttonPrimary: { background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' },
    buttonLight: { background: '#e5e7eb', color: '#111827', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' },
    buttonFatur: { background: '#978729ff', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' },
    buttonGreen: { background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' },

    tableCard: { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' },
    tableScroll: { maxHeight: '60vh', overflow: 'auto', borderRadius: 12, width: '100%' },
    table: { width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 },
    thead: { position: 'sticky', top: 0, background: '#f3f4f6', zIndex: 1 },
    th: {
        textAlign: 'left', padding: '10px 12px',
        borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151', background: '#f3f4f6'
    },
    thSmall: {
        width: 70, textAlign: 'left', padding: '10px 12px',
        borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151', background: '#f3f4f6'
    },
    thActions: {
        width: 160, textAlign: 'left', padding: '12px 14px',
        borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151', background: '#f3f4f6'
    },

    tr: { borderBottom: '1px solid #eef2f7', fontSize: 14 },
    td: { padding: '10px 10px', borderBottom: '1px solid #eef2f7', color: '#111827' },
    tdMono: {
        padding: '10px 10px',
        borderBottom: '1px solid #eef2f7',
        color: '#111827',
        fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    tdClamp: {
        padding: '10px 14px', borderBottom: '1px solid #eef2f7', color: '#111827',
        maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
    },

    tdActions: { padding: '5px 12px', borderBottom: '1px solid #eef2f7' },
    actions: { display: 'flex', gap: 8 },

    actionView: { background: '#3b82f6', border: 'none', color: '#fff', padding: 6, borderRadius: 6, cursor: 'pointer' },
    actionEdit: { background: '#fbbf24', border: 'none', color: '#fff', padding: 6, borderRadius: 6, cursor: 'pointer', marginLeft: 10 },
    actionDelete: { background: '#ef4444', border: 'none', color: '#fff', padding: 6, borderRadius: 6, cursor: 'pointer', marginLeft: 10 },

    pagination: { display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' },
    // page: { border: '1px solid #e5e7eb', background: '#fff', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' },
    pageActive: { border: '1px solid #667eea', background: '#667eea', color: '#fff', padding: '6px 12px', borderRadius: 6 },

    page: { border: '1px solid #e5e7eb', background: '#fff', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' },
    pageActive: { border: '1px solid #667eea', background: '#667eea', color: '#fff', padding: '6px 12px', borderRadius: 6 },
    pageEllipsis: { padding: '6px 12px', color: '#6b7280', userSelect: 'none' }
};

export default ClienteControlScreen;
